/*
 * Copyright (c) 2026.
 * Spin in het Web B.V. Apeldoorn.
 * Deze software mag alleen gebruikt / bekeken / gewijzigd worden volgens de voorwaarden die zijn vastgelegd in een bijbehorende schriftelijke overeenkomst met Spin in het Web B.V.
 */

/**
 * Auteur: Jelmer Jellema - Spin in het Web B.V
 * Gegenereerd met Claude Code (AI), 22-09-2026
 *
 * De ballon met een opmerking van de docent bij een node, in twee standen: tonen (de tekst en de auteur,
 * met een sluitknop die twee klikken vraagt) en bewerken (een tekstvak met een opslaanknop). Vervangt met
 * golf 4d de AngularJS-directive `ballon`.
 *
 * De plek komt van `[dlNodeVolger]`, net als bij de andere overlays op het modelcanvas: het anker is het
 * midden van de node, iets naar linksboven. Alleen de hoek waarin de ballon openklapt rekent hij zelf uit —
 * naar de helft van het canvas waar ruimte is — en dat gedebounced, zoals de directive dat deed. Wat er met
 * de ballon gebeurt (opslaan, wegklikken) beslist het canvas; dit component meldt alleen.
 */
import {
    AfterViewInit,
    ChangeDetectionStrategy,
    Component,
    ElementRef,
    EventEmitter,
    Input,
    OnChanges,
    OnDestroy,
    Output,
    SimpleChanges,
    signal,
    viewChild
} from '@angular/core';
import {TranslatePipe} from '@ngx-translate/core';
import type {Core, NodeSingular} from 'cytoscape';
import {KeyedTimeout} from 'sihw-ngx';
import {DlNodeVolgerDirective, type DlNodeVolgerOffset} from '../../../directives/dl-node-volger/dl-node-volger.directive';

/**
 * De hoek waarin de ballon openklapt, gezien vanaf zijn anker.
 */
interface Ballonhoek {
    verticaal: 'boven' | 'onder';
    horizontaal: 'links' | 'rechts';
}

/**
 * Hoe lang na de laatste beweging de hoek opnieuw bepaald wordt.
 */
const HOEK_NA_MS = 100;

/**
 * Hoe lang de sluitknop na de eerste klik op scherp staat.
 */
const SLUITSTAP_MS = 1500;

@Component({
    selector: 'dl-ballon',
    standalone: true,
    imports: [DlNodeVolgerDirective, TranslatePipe],
    templateUrl: './dl-ballon.component.html',
    styleUrls: ['./dl-ballon.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class DlBallonComponent implements OnChanges, AfterViewInit, OnDestroy {

    /**
     * De node waar de ballon bij hoort.
     */
    @Input({required: true}) node!: NodeSingular;

    /**
     * Het modelcanvas waarin de node leeft.
     */
    @Input({required: true}) cy!: Core;

    /**
     * Tonen of bewerken.
     */
    @Input() modus: 'toon' | 'bewerk' = 'toon';

    /**
     * De tekst: getoond, of in de bewerkstand de tekst waarmee het tekstvak begint.
     */
    @Input() tekst = '';

    /**
     * Wie de opmerking schreef; alleen in de toonstand.
     */
    @Input() auteur = '';

    /**
     * Zonder sluitknop, voor de replay en zonder verbinding.
     */
    @Input() readonly = false;

    /**
     * De sluitknop is twee keer aangeklikt: in de toonstand weghalen, in de bewerkstand annuleren.
     */
    @Output() readonly wegklik = new EventEmitter<void>();

    /**
     * De bewerkte tekst moet opgeslagen worden; een lege tekst haalt de ballon weg.
     */
    @Output() readonly opslaan = new EventEmitter<string>();

    /**
     * De verschuiving van het anker ten opzichte van het midden van de node, zoals de directive hem had.
     */
    protected readonly ankeroffset: DlNodeVolgerOffset = {x: -8, y: -8};

    /**
     * De hoek waarin de ballon nu openklapt.
     */
    protected readonly hoek = signal<Ballonhoek>({verticaal: 'boven', horizontaal: 'rechts'});

    /**
     * Of de sluitknop na een eerste klik op scherp staat; een tweede klik binnen die tijd sluit.
     */
    protected readonly sluitstap = signal(false);

    /**
     * Wat er in het tekstvak staat, in de bewerkstand.
     */
    protected readonly invoer = signal('');

    /**
     * Het tekstvak, zodra het er is; krijgt bij het openen de focus.
     */
    private readonly invoerveld = viewChild<ElementRef<HTMLTextAreaElement>>('invoerveld');

    /**
     * De timers van de hoekbepaling ('hoek') en de sluitknop ('sluit').
     */
    private readonly timers = new KeyedTimeout();

    /**
     * De node en het canvas waarop de luisteraars nu staan.
     */
    private gekoppeld: {node: NodeSingular; cy: Core} | null = null;

    /**
     * Plant een nieuwe hoekbepaling, als de node of het beeld beweegt.
     */
    private readonly planHoek = (): void => {
        this.timers.set('hoek', () => this.bepaalHoek(), HOEK_NA_MS);
    };

    /**
     * Neemt de begintekst over en koppelt de luisteraars opnieuw als de node of het canvas wisselt.
     */
    ngOnChanges(changes: SimpleChanges): void {
        if (changes['tekst']) {
            this.invoer.set(this.tekst);
        }
        if (this.gekoppeld?.node !== this.node || this.gekoppeld?.cy !== this.cy) {
            this.ontkoppel();
            this.koppel();
        }
    }

    /**
     * In de bewerkstand krijgt het tekstvak meteen de focus.
     */
    ngAfterViewInit(): void {
        if (this.modus === 'bewerk') {
            setTimeout(() => this.invoerveld()?.nativeElement.focus());
        }
    }

    /**
     * Ruimt de luisteraars en de timers op.
     */
    ngOnDestroy(): void {
        this.ontkoppel();
        this.timers.clearAll();
    }

    /**
     * Een klik op de sluitknop. De eerste zet hem op scherp, een tweede binnen anderhalve seconde sluit.
     */
    protected klikSluit(): void {
        if (this.sluitstap()) {
            this.timers.clear('sluit');
            this.sluitstap.set(false);
            this.wegklik.emit();
            return;
        }
        this.sluitstap.set(true);
        this.timers.set('sluit', () => this.sluitstap.set(false), SLUITSTAP_MS);
    }

    /**
     * Neemt een wijziging in het tekstvak over.
     */
    protected zetInvoer(event: Event): void {
        this.invoer.set((event.target as HTMLTextAreaElement).value);
    }

    /**
     * De opslaanknop: de tekst gaat naar het canvas.
     */
    protected bewaar(): void {
        this.opslaan.emit(this.invoer());
    }

    /**
     * Zet de luisteraars op de node en het beeld, en bepaalt meteen de hoek.
     */
    private koppel(): void {
        if (!this.node || !this.cy) {
            return;
        }
        this.gekoppeld = {node: this.node, cy: this.cy};
        this.node.on('position', this.planHoek);
        this.cy.on('viewport resize', this.planHoek);
        this.bepaalHoek();
    }

    /**
     * Haalt de luisteraars weg.
     */
    private ontkoppel(): void {
        if (!this.gekoppeld) {
            return;
        }
        this.gekoppeld.node.off('position', undefined, this.planHoek);
        this.gekoppeld.cy.off('viewport resize', this.planHoek);
        this.gekoppeld = null;
    }

    /**
     * Klapt de ballon open naar de helft van het canvas waar ruimte is: staat het anker in de bovenste
     * helft, dan eronder, en in de linker helft naar rechts.
     */
    private bepaalHoek(): void {
        const gekoppeld = this.gekoppeld;
        if (!gekoppeld || !gekoppeld.node.inside()) {
            return;
        }
        const bb = gekoppeld.node.renderedBoundingBox({includeLabels: false});
        const links = bb.x1 + bb.w / 2 + (this.ankeroffset.x ?? 0);
        const boven = bb.y1 + bb.h / 2 + (this.ankeroffset.y ?? 0);
        const container = gekoppeld.cy.container();
        this.hoek.set({
            verticaal: container && boven < container.clientHeight / 2 ? 'onder' : 'boven',
            horizontaal: container && links < container.clientWidth / 2 ? 'rechts' : 'links'
        });
    }
}
